// LAP-733 (GE022) round 11 — fully translated + redesigned account settings.
// Changes vs the previous implementation:
//   * all visible strings now go through useTranslation('layout') so the page
//     follows the active interface language;
//   * the placeholder "Connexions" block (which was rendering a hard-coded
//     "Iphone XR / Selestat, France / app Life -il y a 2 heures" with no
//     real data source) has been removed entirely;
//   * each tab now uses a Card + Stack layout with consistent spacing, real
//     TextField labels (instead of the old left-floating <label> sitting next
//     to <Input/>), and the action footer uses a Save/Cancel pair with a
//     primary CTA instead of a flat-white text button on a navy bar;
//   * moment.locale is synced to the active i18n language so "11 mayo. 2026"
//     no longer appears when the rest of the UI is in another language.
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  Slide,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { TabContext } from '@mui/lab'
import PersonIcon from '@mui/icons-material/Person'
import PublicIcon from '@mui/icons-material/Public'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseIcon from '@mui/icons-material/Close'

import moment from 'moment'
import 'moment/locale/fr'
import 'moment/locale/de'
import 'moment/locale/it'
import 'moment/locale/es'

import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import UserPassword from '../UserPassword'
import UploadProfile from '../UploadProfile'
import { Input } from '../Input'
import ComboBox from '../ComboBox'
import { convertToMuiDate } from 'utils/helpers'
import { makeGetRequest, makePostRequest } from 'utils/api'
import { setSiteUser } from 'reduxLib/slices/userSlice'

// Visual constants ---------------------------------------------------------
const NAVY = '#001B42'
const ACCENT = '#2563EB'

function TabPanel({ children, value, index, dense }) {
  if (value !== index) return null
  return <Box sx={{ pt: dense ? 1 : 3 }}>{children}</Box>
}
TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  dense: PropTypes.bool,
}

function SectionRow({ icon, label, children }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" width="100%">
      <Box sx={{ color: NAVY, opacity: 0.85 }}>{icon}</Box>
      <Stack spacing={0.5} sx={{ flex: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {label}
        </Typography>
        {children}
      </Stack>
    </Stack>
  )
}
SectionRow.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
}

export default function CompteUtilisateur({
  appUser,
  setAppUser, // eslint-disable-line no-unused-vars
  handleClose,
  fetchUser,
  countrys = [],
}) {
  const { t, i18n: i18nInstance } = useTranslation('layout')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [tabValue, setTabValue] = useState(0)
  const [userVal, setUserVal] = useState(appUser)
  const [selectedGenre, setSelectedGenre] = useState(null)
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedCurrency, setSelectedCurrency] = useState(null)
  const [currencies, setCurrencies] = useState([])
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })

  // Keep moment.js aligned with the active interface language so dates
  // ("Date de création de votre compte" / "Account created on" / ...) render
  // in the right locale.
  useEffect(() => {
    const lang = (i18nInstance.language || 'fr').split('-')[0]
    moment.locale(['fr', 'en', 'de', 'it', 'es'].includes(lang) ? lang : 'fr')
  }, [i18nInstance.language])

  // Translated gender options, recomputed when the language changes.
  const userGenre = useMemo(
    () => [
      { id: 1, label: t('settings.userAccount.personalInfo.genderOptions.male'), value: 'male' },
      { id: 2, label: t('settings.userAccount.personalInfo.genderOptions.female'), value: 'female' },
      { id: 3, label: t('settings.userAccount.personalInfo.genderOptions.other'), value: 'other' },
    ],
    [t],
  )

  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      nom: appUser?.nom,
      prenom: appUser?.prenom,
      email: appUser?.email,
      genre: appUser?.genre,
      img: appUser?.img,
      img_url: appUser?.img_url,
      marital_status: appUser?.marital_status,
      numero_de_mobile: appUser?.numero_de_mobile,
      vile: appUser?.vile,
      pays_id: appUser?.pays_id,
      currency: appUser?.currency,
      date_de_naissance: appUser?.date_de_naissance
        ? convertToMuiDate(appUser?.date_de_naissance)
        : '',
    },
  })

  useEffect(() => {
    const match = userGenre.find((e) => appUser.genre === e.value) || null
    setSelectedGenre(match)
    setValue('genre', match)
    getCurrencies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGenre])

  useEffect(() => {
    if (appUser.pays_id && countrys.length > 0) {
      const pays = countrys.find((e) => e.CountryCode === appUser.pays_id)
      if (pays) {
        setSelectedCountry(pays)
        setValue('pays_id', pays.CountryCode)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countrys])

  useEffect(() => {
    if (appUser.currency && currencies.length > 0) {
      const currency = currencies.find((e) => e.id === appUser.currency)
      if (currency) {
        setSelectedCurrency(currency)
        setValue('currency', currency.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencies])

  const getCurrencies = async () => {
    const res = await makeGetRequest('/api/Finance/devise-list/', {}, true)
    if (res.status === 200) {
      const data = res.data.map((e) => ({ ...e, label: `${e.Code_iso} | ${e.Designation}` }))
      setCurrencies(data)
    }
  }

  const onSubmit = async (data) => {
    let payload = { ...data }
    if (data.img !== userVal.img) {
      payload = { ...payload, img: userVal.img }
    }
    payload = { ...payload, genre: data.genre?.value ?? data.genre }

    if (selectedCountry) payload.pays_id = selectedCountry.CountryCode
    else if (data.pays_id) payload.pays_id = data.pays_id

    if (selectedCurrency) payload.currency = selectedCurrency.id
    else if (data.currency) payload.currency = data.currency

    delete payload.img_url

    const response = await makePostRequest('/api/user/update-profile/', payload, {}, true)
    if (response.status === 200 && response.data.status) {
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('settings.userAccount.saveSuccess'),
      })
      fetchUser()
      handleClose()
    }
  }

  const handleConfirmExport = async () => {
    setExportLoading(true)
    try {
      const response = await makePostRequest('/api/user/export/', {}, {}, true)
      if (response.status === 200 && response.data.status) {
        setSnackbar({
          open: true,
          severity: 'info',
          message: t('settings.userAccount.security.exportToasts.queued'),
        })
        pollExportStatus(response.data.export_id)
      } else if (response.status === 429) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message:
            response.data.message ||
            t('settings.userAccount.security.exportToasts.throttled'),
        })
        setExportLoading(false)
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message:
            response.data.message || t('settings.userAccount.security.exportToasts.error'),
        })
        setExportLoading(false)
      }
    } catch (e) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: t('settings.userAccount.security.exportToasts.requestError'),
      })
      setExportLoading(false)
    }
    setExportDialogOpen(false)
  }

  const pollExportStatus = async (exportId) => {
    const maxAttempts = 30
    let attempts = 0
    const check = async () => {
      try {
        const response = await makeGetRequest('/api/user/export/status/', {}, true)
        if (response.status === 200 && response.data.status) {
          const { export_status, can_download, error_message } = response.data
          if (export_status === 'COMPLETED' && can_download) {
            setSnackbar({
              open: true,
              severity: 'success',
              message: t('settings.userAccount.security.exportToasts.ready'),
            })
            setExportLoading(false)
            downloadExportFile(exportId)
            return
          }
          if (export_status === 'FAILED') {
            setSnackbar({
              open: true,
              severity: 'error',
              message:
                error_message || t('settings.userAccount.security.exportToasts.failed'),
            })
            setExportLoading(false)
            return
          }
          attempts += 1
          if (attempts < maxAttempts) setTimeout(check, 2000)
          else {
            setSnackbar({
              open: true,
              severity: 'warning',
              message: t('settings.userAccount.security.exportToasts.timeout'),
            })
            setExportLoading(false)
          }
        }
      } catch (e) {
        setExportLoading(false)
        setSnackbar({
          open: true,
          severity: 'error',
          message: t('settings.userAccount.security.exportToasts.statusError'),
        })
      }
    }
    check()
  }

  const downloadExportFile = async (exportId) => {
    try {
      const token = localStorage.getItem('token')
      const baseUrl = process.env.REACT_APP_LIFE_BACKEND_URL || 'http://192.168.8.100:8000'
      const response = await fetch(`${baseUrl}/api/user/export/download/${exportId}/`, {
        method: 'GET',
        headers: { Authorization: `Token ${token}` },
      })
      if (response.ok) {
        const contentDisposition = response.headers.get('Content-Disposition') || ''
        const match = contentDisposition.match(/filename="?(.+?)"?$/)
        const filename = (match && match[1]) || 'Life_UserDataExport.zip'
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: t('settings.userAccount.security.exportToasts.downloadError'),
        })
      }
    } catch (e) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: t('settings.userAccount.security.exportToasts.downloadError'),
      })
    }
  }

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }))

  return (
    <>
      {deleteOpen && (
        <DeleteAccount setModelOpen={setDeleteOpen} t={t} />
      )}
      <ExportConfirmDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onConfirm={handleConfirmExport}
        t={t}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <Box display="flex" flexDirection="column" height="100%" sx={{ bgcolor: '#F7F9FC' }}>
          {/* ---- Header banner with avatar ------------------------------- */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${NAVY} 0%, #0A2A66 100%)`,
              color: 'white',
              p: isMobile ? 2 : 3,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={<UploadProfile setUserVal={setUserVal} />}
            >
              <Avatar
                sx={{ height: 56, width: 56, border: '2px solid rgba(255,255,255,0.6)' }}
                alt={`${appUser?.prenom || ''} ${appUser?.nom || ''}`}
                src={userVal?.img_url || 'https://cdn-icons-png.flaticon.com/512/147/147140.png'}
              />
            </Badge>
            <Box ml={2.5}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {`${appUser?.prenom || ''} ${appUser?.nom || ''}`.trim()}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {appUser?.email}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{ ml: 'auto', color: 'white' }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* ---- Tabs ----------------------------------------------------- */}
          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                  '& .Mui-selected': { color: ACCENT },
                  '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                }}
              >
                <Tab label={t('settings.userAccount.tabs.general')} />
                <Tab label={t('settings.userAccount.tabs.security')} />
                <Tab label={t('settings.userAccount.tabs.personalInfo')} />
              </Tabs>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: isMobile ? 2 : 4, py: 3 }}>
              {/* General tab */}
              <TabPanel value={tabValue} index={0}>
                <Card elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
                  <CardContent>
                    <Stack spacing={2.5}>
                      <Input
                        control={control}
                        inputProps={{
                          variant: 'outlined',
                          type: 'text',
                          required: true,
                          fullWidth: true,
                          label: t('settings.userAccount.general.lastName'),
                        }}
                        rules={{ required: true }}
                        name="nom"
                      />
                      <Input
                        control={control}
                        inputProps={{
                          variant: 'outlined',
                          type: 'text',
                          required: true,
                          fullWidth: true,
                          label: t('settings.userAccount.general.firstName'),
                        }}
                        rules={{ required: true }}
                        name="prenom"
                      />
                      <Input
                        control={control}
                        inputProps={{
                          variant: 'outlined',
                          type: 'tel',
                          required: true,
                          fullWidth: true,
                          label: t('settings.userAccount.general.phone'),
                        }}
                        rules={{ required: true }}
                        name="numero_de_mobile"
                      />
                      <Input
                        control={control}
                        inputProps={{
                          variant: 'outlined',
                          type: 'email',
                          required: true,
                          fullWidth: true,
                          label: t('settings.userAccount.general.email'),
                        }}
                        rules={{ required: true }}
                        name="email"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </TabPanel>

              {/* Security tab — note: the placeholder "Connexions" block has
                  been deliberately removed since we don't yet track real
                  device/session metadata server-side. */}
              <TabPanel value={tabValue} index={1}>
                <Card elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
                  <CardContent>
                    <Stack spacing={3}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            bgcolor: 'rgba(37,99,235,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: ACCENT,
                          }}
                        >
                          <LockOutlinedIcon />
                        </Box>
                        <Stack spacing={0.5} sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {t('settings.userAccount.security.passwordTitle')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ************
                          </Typography>
                        </Stack>
                        <UserPassword />
                      </Stack>

                      <Divider />

                      <Stack
                        direction={isMobile ? 'column' : 'row'}
                        spacing={2}
                        sx={{ pt: 1 }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={
                            exportLoading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <DownloadIcon />
                            )
                          }
                          onClick={() => setExportDialogOpen(true)}
                          disabled={exportLoading}
                          sx={{ textTransform: 'none' }}
                        >
                          {exportLoading
                            ? t('settings.userAccount.security.downloadDataLoading')
                            : t('settings.userAccount.security.downloadData')}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => setDeleteOpen(true)}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('settings.userAccount.security.deleteAccount')}
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </TabPanel>

              {/* Personal info tab */}
              <TabPanel value={tabValue} index={2}>
                <Card elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
                  <CardContent>
                    <Box
                      display="grid"
                      gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'}
                      columnGap={3}
                      rowGap={3}
                    >
                      <SectionRow
                        icon={<CalendarTodayIcon fontSize="small" />}
                        label={t('settings.userAccount.personalInfo.accountCreated')}
                      >
                        <Typography>
                          {appUser?.created_att
                            ? moment(appUser.created_att).format('LL')
                            : '—'}
                        </Typography>
                      </SectionRow>

                      <SectionRow
                        icon={<PublicIcon fontSize="small" />}
                        label={t('settings.userAccount.personalInfo.country')}
                      >
                        <ComboBox
                          handleAutoComplete={(_, j) => {
                            setValue('pays_id', j?.CountryCode)
                            setSelectedCountry(j)
                          }}
                          data={countrys}
                          name="pays_id"
                          required
                          selectedValue={selectedCountry}
                        />
                      </SectionRow>

                      <SectionRow
                        icon={<LocationCityIcon fontSize="small" />}
                        label={t('settings.userAccount.personalInfo.city')}
                      >
                        <Input
                          control={control}
                          inputProps={{
                            variant: 'outlined',
                            type: 'text',
                            required: true,
                            fullWidth: true,
                            size: 'small',
                          }}
                          rules={{ required: true }}
                          name="vile"
                        />
                      </SectionRow>

                      <SectionRow
                        icon={<AttachMoneyIcon fontSize="small" />}
                        label={t('settings.userAccount.personalInfo.currency')}
                      >
                        <ComboBox
                          handleAutoComplete={(_, j) => {
                            setValue('currency', j?.id)
                            setSelectedCurrency(j)
                          }}
                          data={currencies}
                          name="currency"
                          required={false}
                          selectedValue={selectedCurrency}
                        />
                      </SectionRow>

                      <SectionRow
                        icon={<CalendarTodayIcon fontSize="small" />}
                        label={t('settings.userAccount.personalInfo.birthdate')}
                      >
                        <Input
                          control={control}
                          inputProps={{
                            variant: 'outlined',
                            type: 'date',
                            required: true,
                            fullWidth: true,
                            size: 'small',
                          }}
                          rules={{ required: true }}
                          name="date_de_naissance"
                        />
                      </SectionRow>

                      <SectionRow
                        icon={<PersonIcon fontSize="small" />}
                        label={t('settings.userAccount.personalInfo.gender')}
                      >
                        <ComboBox
                          handleAutoComplete={(_, j) => {
                            setValue('genre', j)
                            setSelectedGenre(j)
                          }}
                          data={userGenre}
                          name="genre"
                          required
                          selectedValue={selectedGenre}
                        />
                      </SectionRow>
                    </Box>
                  </CardContent>
                </Card>
              </TabPanel>
            </Box>
          </TabContext>

          {/* ---- Sticky footer with the primary CTAs --------------------- */}
          <Box
            sx={{
              borderTop: '1px solid #E5E7EB',
              bgcolor: 'white',
              px: isMobile ? 2 : 3,
              py: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="text"
                onClick={handleClose}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                {t('settings.userAccount.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  bgcolor: ACCENT,
                  '&:hover': { bgcolor: '#1D4ED8' },
                  px: 3,
                }}
              >
                {t('settings.userAccount.save')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </form>
    </>
  )
}

CompteUtilisateur.propTypes = {
  appUser: PropTypes.object.isRequired,
  setAppUser: PropTypes.func,
  handleClose: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  countrys: PropTypes.array,
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

function ExportConfirmDialog({ open, onClose, onConfirm, t }) {
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="export-dialog-description"
    >
      <DialogContent>
        <DialogContentText id="export-dialog-description">
          {t('settings.userAccount.security.exportDialog.body')}
          <br />
          <br />
          {t('settings.userAccount.security.exportDialog.expiry')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('settings.userAccount.security.exportDialog.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          sx={{ textTransform: 'none' }}
        >
          {t('settings.userAccount.security.exportDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
ExportConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}

function DeleteAccount({ setModelOpen, t }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [open, setOpen] = useState(true)

  const handleCloseLocal = () => {
    setOpen(false)
    setModelOpen(false)
  }
  const handleConfirm = async () => {
    const response = await makePostRequest('/api/user/delete-account/', {}, {}, true)
    handleCloseLocal()
    localStorage.removeItem('token')
    dispatch(setSiteUser({}))
    navigate('/login')
    // eslint-disable-next-line no-unused-expressions
    response
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleCloseLocal}
      aria-describedby="delete-account-description"
    >
      <DialogContent>
        <DialogContentText id="delete-account-description">
          {t('settings.userAccount.security.deleteDialog.body')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseLocal} sx={{ textTransform: 'none' }}>
          {t('settings.userAccount.security.deleteDialog.no')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          sx={{ textTransform: 'none' }}
        >
          {t('settings.userAccount.security.deleteDialog.yes')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
DeleteAccount.propTypes = {
  setModelOpen: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}
